import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { accessAddressQueries } from "~/api/access-address/queries";
import { AreaRentalMomentumOverview } from "~/routes/(restricted)/ejendomstjek/address/$accessAddressId/-components/AreaRentalMomentum/AreaRentalMomentumOverview";
import { UnitHeader } from "~/routes/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/rent-analysis/-components/UnitList/UnitHeader/UnitHeader";
import { CurrentRentForm } from "~/routes/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/rent-analysis/-components/dialogs/CurrentRentForm";
import {
  currentRentDialogOpenAtom,
  focusedUnitIdAtom,
} from "~/routes/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/rent-analysis/-state/current-rent-atoms";
import { actionOnMissingAccess } from "~/utils/access";
import { UnitList } from "../-components/UnitList/UnitList";
import { UnitRentingOutDetails } from "./-components/UnitRentingOutDetails";

export const Route = createFileRoute(
  "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/renting-out/{-$unitId}",
)({
  beforeLoad: ({ context: { authClient }, params }) => {
    actionOnMissingAccess(authClient.user, "property-check", () => {
      throw redirect({
        to: "/ejendomstjek/address/$accessAddressId/{-$placeId}/overview/{-$unitId}",
        params,
      });
    });
  },
  component: RouteComponent,
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("renting-out");

    const title = i18n.t("renting-out:meta.title");

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
});

function RouteComponent() {
  const { accessAddressId, placeId, unitId } = Route.useParams();

  const [isDialogOpen, setIsDialogOpen] = useAtom(currentRentDialogOpenAtom);
  const focusedUnitId = useAtomValue(focusedUnitIdAtom);

  const { data: unitsData, isLoading: isLoadingUnits } = useQuery({
    ...accessAddressQueries.units(accessAddressId, placeId),
    throwOnError: true,
  });

  return (
    <div className="flex flex-col gap-6">
      <AreaRentalMomentumOverview accessAddressId={accessAddressId} />

      <UnitList
        items={unitsData?.units}
        isLoading={isLoadingUnits}
        getItemId={(unit) => unit.unitId}
        currentItemId={unitId}
        isItemExpandable={(unit) => unit.isResidential}
        renderHeader={(unit, opts) => (
          <UnitHeader
            unit={unit}
            accessAddressId={accessAddressId}
            {...opts}
          />
        )}
        renderExpanded={(unit) => (
          <UnitRentingOutDetails
            unit={unit}
            accessAddressId={accessAddressId}
            isExpanded={unitId === unit.unitId}
          />
        )}
      />

      <CurrentRentForm
        accessAddressId={accessAddressId}
        placeId={placeId}
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        focusedUnitId={focusedUnitId}
      />
    </div>
  );
}
